import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { AuthLayout } from "@/pages/Login";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Forgot your password?" subtitle="We'll email you a link to choose a new one.">
      {sent ? (
        <div className="space-y-4" data-testid="forgot-sent">
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            If that email has a FindRxStaff account, a reset link is on its way. It works once and
            expires in an hour.
          </p>
          <Link to="/login" className="btn-primary w-full" data-testid="forgot-back-to-login">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <label className="field-label" htmlFor="forgot-email">Email</label>
            <input
              id="forgot-email"
              className="field-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
            />
          </div>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">{error}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="forgot-submit">
            {busy ? "Sending…" : "Email me a reset link"}
          </button>
          <Link to="/login" className="block text-sm font-medium text-brand" data-testid="forgot-to-login">
            Back to sign in
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Both passwords must match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Choose a new password" subtitle="Pick something at least 6 characters long.">
      {done ? (
        <div className="space-y-4" data-testid="reset-done">
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Your password has been reset. Taking you to sign in…
          </p>
          <Link to="/login" className="btn-primary w-full" data-testid="reset-to-login">
            Sign in now
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          {!token && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="reset-no-token">
              This link is missing its reset code. Request a new link from the sign-in page.
            </p>
          )}
          <div>
            <label className="field-label" htmlFor="new-password">New password</label>
            <input
              id="new-password"
              className="field-input"
              type="password"
              minLength={6}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password"
              className="field-input"
              type="password"
              minLength={6}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm"
            />
          </div>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy || !token} className="btn-primary w-full" data-testid="reset-submit">
            {busy ? "Saving…" : "Save new password"}
          </button>
          <Link to="/forgot-password" className="block text-sm font-medium text-brand" data-testid="reset-request-new">
            Request a new link
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}
